import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { Patient } from 'src/app/models/patient.model';
import { EditOrderComponent } from 'src/app/practice/practice-devices/modals/edit-order/edit-order.component';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { DeviceOrderingService } from 'src/app/services/device-ordering/device-ordering.service';
import { OrderRequestVO } from 'src/app/services/device-ordering/value-objects/order.vo';
import { FirestoreService } from 'src/app/services/firestore.service';
import { InitialDataModel } from 'src/app/services/models/initial-data-model.service';
import { SnackService } from 'src/app/services/snack.service';
import { UtilsService } from 'src/app/services/utils.service';
import { ConfirmOrderDialogComponent } from '../../../practice/practice-devices/modals/confirm-order-dialog/confirm-order-dialog.component';
import { RejectOrderDialogComponent } from '../../../practice/practice-devices/modals/reject-order-dialog/reject-order-dialog.component';
import { PatientNamePipe } from '../../pipes/patient-name.pipe';
import { OrderRequestsPaginatorIntl } from './order-requests-paginator-intl';
import { EditSupplyOrderComponent } from '../../../practice/practice-devices/modals/edit-supply-order/edit-supply-order.component';
@Component({
  selector: 'app-device-order-request-table',
  templateUrl: './device-order-request-table.component.html',
  styleUrls: ['./device-order-request-table.component.scss'],
  providers: [OrderRequestsPaginatorIntl, { provide: MatPaginatorIntl, useExisting: OrderRequestsPaginatorIntl }],
})
export class DeviceOrderRequestTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  orderRequestsDevicesColumns = [
    'patientLabel',
    'requestLabel',
    'category',
    'created_at',
    'clientLabel',
    'comments',
    'requesterLabel',
    'last_updated',
    'actions',
  ];
  orderRequestDevices$: Observable<any>;
  currentUser: Patient;
  filterForm: UntypedFormGroup;
  pageSize = 20;
  pageSizeOptions = [20, 30, 50];
  maxClientAccountByProvider = 8;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  orderRequestByStates: any[] = [
    { label: 'All', value: null },
    { label: 'Approved', value: 'true' },
    { label: 'Rejected', value: 'false' },
    { label: 'Pending', value: 'pending' },
  ];
  unsubscribe = new Subject<void>();
  loading = false;
  // pageCursors[i] is the last doc of page i - 1 (startAfter cursor); page 0 has none.
  private pageCursors: any[] = [null];
  private page$ = new BehaviorSubject<{ index: number; size: number }>({ index: 0, size: this.pageSize });

  rowMenuOptions = [
    {
      label: 'Open patient chart',
      icon: 'open_in_new',
      action: (data: any) => window.open(`clinical/patient-detail/${data}`, '_blank'),
    },
  ];

  constructor(
    private fs: FirestoreService,
    private auth: NewAuthService,
    private dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private utilService: UtilsService,
    private initialData: InitialDataModel,
    private deviceService: DeviceOrderingService,
    private router: Router,
    private snackService: SnackService,
    private patientNamePipe: PatientNamePipe,
    private paginatorIntl: OrderRequestsPaginatorIntl
  ) {
    this.filterForm = this.fb.group({
      searchText: [''],
      orderRequestByState: [this.orderRequestByStates[3]],
    });
  }

  ngOnInit(): void {
    this.currentUser = this.auth.user;
    this.initialData.getProviders().then(() => {
      this.getOrderRequests();
      this.setFilterListener();
    });
  }

  ngAfterViewInit(): void {
    // Assigned once: re-assigning it on every snapshot re-sorts the rows. Sort and search apply to the
    // current page; paging itself runs in Firestore (onPage), so the data source gets no paginator.
    this.dataSource.sort = this.sort;
  }

  getOrderRequests(): void {
    this.loading = true;
    const stateControl = this.filterForm.get('orderRequestByState');
    // State filter and paging run in Firestore: listening to the whole collection (~9k docs in prod,
    // ~7k of them approved) re-processed every row on each write and froze the page.
    this.orderRequestDevices$ = stateControl.valueChanges.pipe(
      startWith(stateControl.value),
      distinctUntilChanged((prev, curr) => prev?.value === curr?.value),
      tap(() => {
        this.loading = true;
        this.paginator?.firstPage();
      }),
      switchMap((state) => this.queryPages(state?.value)),
      map((orders) => {
        const transformedOrders = orders.map((data) => {
          const clientId = data?.client_responsible_id;
          return {
            manufacturerId: data.request_body?.manufacturerId,
            requestLabel: data.type
              ? data.request_body?.supply_name
              : `${data.request_body?.manufacturerId} - ${data.request_body?.device.sku_display}`,
            category: data.type ? 'Supply' : 'Device',
            patientLabel: this.patientNamePipe.transform(
              data.request_body?.patient?.firstName,
              data.request_body?.patient?.lastName,
              true
            ),
            created_at: data.createdAt ?? data.created_at,
            updated_at: data.updatedAt ?? data.updated_at,
            approved: data.approved,
            patient: data.request_body.patient,
            device: data.request_body.device,
            clientLabel: clientId ? this.initialData.clients[clientId]?.client_name : 'N/A',
            requesterLabel: `${this.initialData.allClientUsers[data.requester_id]?.firstName ?? 'N/A'} ${
              this.initialData.allClientUsers[data.requester_id]?.lastName ?? '' ?? 'N/A'
            }`,
            ...data,
          };
        });
        this.dataSource.data = transformedOrders;
        this.loading = false;
        return this.dataSource;
      })
    );
  }

  /**
   * Every view is paged in Firestore, newest `createdAt` first (every writer sets `createdAt`;
   * `created_at` is legacy). `state` is the stored `approved` value ('pending' | 'true' | 'false');
   * null loads every state.
   */
  queryPages(state: string | null): Observable<any[]> {
    const sources = this.pageSources(state);
    this.pageCursors = [null];
    this.page$.next({ index: 0, size: this.paginator?.pageSize ?? this.pageSize });
    return this.page$.pipe(
      switchMap(({ index, size }) => {
        const page$ = !sources.length
          ? of({ page: [], hasNextPage: false })
          : sources.length === 1
          ? this.queryPage(sources[0], index, size)
          : this.queryMergedPage(sources, index, size);
        return page$.pipe(
          map(({ page, hasNextPage }) => {
            this.setPaginator(index, size, page.length, hasNextPage);
            return page;
          }),
          // Caught per page so a failed query doesn't end the stream: paging and the toggle keep working.
          catchError((error) => {
            console.error('Error loading order requests:', error);
            this.snackService.genericSnackBar('Error loading order requests', ['error-snackbar'], 5000);
            return of([]);
          })
        );
      })
    );
  }

  /** One query per chunk of clients for non-admins (a Firestore `in` takes up to 8 here). */
  pageSources(state: string | null): ((ref: any) => any)[] {
    const byState = (ref: any) => (state ? ref.where('approved', '==', state) : ref);
    if (this.currentUser.roles.isAdmin) {
      return [byState];
    }
    const clients: string[] = this.initialData.clientAccounts ?? [];
    const chunks =
      clients.length <= this.maxClientAccountByProvider
        ? [clients].filter((chunk) => chunk.length)
        : this.utilService.getChunksWhenArrayLengthIsGreaterThanMaxValue(clients);
    return chunks.map((chunk) => (ref: any) => byState(ref.where('client_responsible_id', 'in', chunk)));
  }

  queryPage(source: (ref: any) => any, index: number, size: number): Observable<{ page: any[]; hasNextPage: boolean }> {
    const cursor = this.pageCursors[index];
    return this.fs
      .colWithIds$(`hardware_order_requests`, (ref) => {
        const ordered = source(ref).orderBy('createdAt', 'desc');
        // One extra row tells whether there is a next page.
        return (cursor ? ordered.startAfter(cursor) : ordered).limit(size + 1);
      })
      .pipe(
        map((docs) => {
          const hasNextPage = docs.length > size;
          const page = docs.slice(0, size);
          if (hasNextPage) {
            this.pageCursors[index + 1] = page[page.length - 1].doc;
          }
          return { page, hasNextPage };
        })
      );
  }

  /**
   * A cursor can't be shared across chunk queries, so each chunk reads its first (index + 1) * size + 1
   * docs and the page is cut from the merge. The read grows with the page number, which is fine for the
   * few users with more than 8 clients.
   */
  queryMergedPage(
    sources: ((ref: any) => any)[],
    index: number,
    size: number
  ): Observable<{ page: any[]; hasNextPage: boolean }> {
    const end = (index + 1) * size;
    const millis = (date: any) => date?.toMillis?.() ?? 0;
    return combineLatest(
      sources.map((source) =>
        this.fs.colWithIds$('hardware_order_requests', (ref) => source(ref).orderBy('createdAt', 'desc').limit(end + 1))
      )
    ).pipe(
      map((results) => {
        const merged = results
          .reduce((acc, curr) => [...acc, ...curr], [] as any[])
          .sort((a, b) => millis(b.createdAt) - millis(a.createdAt));
        return { page: merged.slice(index * size, end), hasNextPage: merged.length > end };
      })
    );
  }

  /** Firestore only says whether a next page exists, so the total is known on the last page only. */
  setPaginator(index: number, size: number, rows: number, hasNextPage: boolean): void {
    this.paginatorIntl.setTotalKnown(!hasNextPage);
    if (this.paginator) {
      this.paginator.pageIndex = index;
      this.paginator.length = index * size + rows + (hasNextPage ? 1 : 0);
    }
  }

  onPage(event: PageEvent): void {
    this.loading = true;
    if (event.pageSize !== this.page$.value.size) {
      // Cursors depend on the page size.
      this.pageCursors = [null];
      this.page$.next({ index: 0, size: event.pageSize });
      return;
    }
    this.page$.next({ index: event.pageIndex, size: event.pageSize });
  }

  openOrderForm(data: any, event: Event): void {
    event.stopPropagation();
    if (data.doc) {
      delete data?.doc;
    }
    const dialogRef = this.dialog.open(ConfirmOrderDialogComponent, {
      width: '600px',
      minHeight: '400px',
      panelClass: 'welby-modal-scroll',
      data,
    });
  }

  openRejectDialog(data: any, event: Event): void {
    event.stopPropagation();
    if (data.doc) {
      delete data?.doc;
    }
    const dialogRef = this.dialog.open(RejectOrderDialogComponent, {
      width: '500px',
      data,
    });
  }

  setFilterListener(): void {
    this.filterForm
      .get('searchText')
      .valueChanges.pipe(debounceTime(300), takeUntil(this.unsubscribe), distinctUntilChanged())
      .subscribe((value) => {
        // Filters the current page only; searching every order is a separate ticket.
        this.dataSource.filter = value.trim().toLowerCase();
      });
  }

  async editOrderRequest(orderRequest: OrderRequestVO, event: Event): Promise<void> {
    event.stopPropagation();
    const order = await this.deviceService.getOrderById(orderRequest.id).pipe(take(1)).toPromise();
    if (order) {
      const dialogRef = this.dialog.open(EditOrderComponent, {
        width: '500px',
        data: order,
      });
    } else {
      this.snackService.genericSnackBar('Order not found', ['error-snackbar'], 5000);
    }
  }

  editSupplyOrderRequest(orderRequest: OrderRequestVO, event: Event): void {
    event.stopPropagation();
    if (orderRequest) {
      const dialogRef = this.dialog.open(EditSupplyOrderComponent, {
        width: '500px',
        data: orderRequest,
      });
    } else {
      this.snackService.genericSnackBar('Order not found', ['error-snackbar'], 5000);
    }
  }

  viewPatientDetail(patientId: string) {
    this.router.navigate([`clinical/patient-detail`, patientId]);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
